/* eslint-disable max-len */
import React, { useContext, useState, useEffect } from 'react';
import {
  oneOf,
  bool as boolType,
  any,
  shape as shapeType,
  string as stringType,
} from 'prop-types';
import { ExperienceContext, useConfigService } from '@thd-nucleus/experience-context';
import {
  bool,
  extend,
  string,
  shape,
  QueryProvider,
  params
} from '@thd-nucleus/data-sources';
import { ProductPod } from '@thd-olt-component-react/product-pod-v6';
import { Image } from '@thd-olt-component-react/core-ui';
import { Carousel } from '@one-thd/sui-carousel';
import classNames from 'classnames';
import { Typography } from '@one-thd/sui-atomic-components';
import { LoadingPlaceholder } from './LoadingPlaceholder';
import { CarouselHeader } from './CarouselHeader';
import { RequiredAttributes } from './RequiredAttributes';
import { getSkippers, isAppliance } from '../utils/helpers';
import VisualTabs from './VisualTabs';
import CollectionsImage from './CollectionsImage';
import { FallbackCard } from './FallbackCard';

// the QueryProvider on line 54 doesnt get access to ProductPod because data?.products is empty at first
// this is what hoist() was originally intended for but the problem with hoist() is that it mutates
// components and runs into issues when they are in multiple places
// eslint-disable-next-line react/prop-types
const Wrapper = ({ children }) => children;
Wrapper.dataModel = extend({
  product: params({ itemId: string().isRequired() }).shape({
    dataSource: string(),
  })
}, ProductPod);
const RecommendationsProductPodCarousel = (props) => {
  const { channel } = useContext(ExperienceContext);

  const isMobile = channel === 'mobile';

  const {
    anchorImage,
    data,
    hideTitle,
    loading,
    scheme,
    showLoading,
    storeId,
    showFallback,
    fallbackInfo,
    userId,
    subtitle,
    isCategoryTab, // Displaying the category tabs
    // productPodProps,
    brandTitleMaxLine,
    customEventObject,
    hideATC,
    hideBulkPrice,
    hideFavorites,
    hideFulfillment,
    hideLimitPerOrder,
    hidePriceBadge,
    hideRating,
    hideRebates,
    hideShippingInfo,
    hideShopThisCollection,
    showSubscribeAndSave,
    membershipInformation,
    zipCode,
    slidesPer,
    keepAddToCart,
    noATCFulfillment,
    hideSwatches,
    hideCarouselArrows
  } = props;

  let hideAddToCart;

  let recsCollectionTabCount = useConfigService('fs:recsCollectionTabCount') || 4;

  if (keepAddToCart === true) {
    hideAddToCart = false; // show ATC always - including MW
  } else if (hideATC || isMobile) {
    hideAddToCart = true;
  }

  const defaultVariables = {
    storeId,
    zipCode,
    installationServices: false,
    loyaltyMembershipInput: membershipInformation?.data?.loyaltyMembership || null,
    ...getSkippers({ skipSubscribeAndSave: !showSubscribeAndSave })
  };

  const { products, metadata, categories } = data || {};

  const classes = classNames('sui-grid', 'sui-gap-4', 'sui-grid-cols-1', { 'sui-grid-cols-3': metadata?.collectionImageUrl || anchorImage || isCategoryTab });

  const titleClasses = classNames('sui-grid', 'sui-gap-4', 'sui-grid-cols-1', { 'sm:sui-grid-cols-3': metadata?.collectionImageUrl || anchorImage || isCategoryTab });

  const colSpanClasses = classNames({ 'sui-col-span-2': metadata?.collectionImageUrl || anchorImage || isCategoryTab });

  let title = metadata?.collectionName || metadata?.title;

  const [filtered, setFiltered] = useState([]);
  const [isCategorySelected, setIsCategorySelected] = useState(false);

  const pullFilteredData = (filteredProducts) => {
    setIsCategorySelected(true);
    setFiltered(filteredProducts);
  };

  let displayProducts;

  if (isCategorySelected) {
    displayProducts = filtered;
  } else {
    displayProducts = products;
  }

  let breakpoints = ({});
  if (!slidesPer) {
    breakpoints = ({
      sm: {
        slidesPerView: 2,
        slidesPerGroup: 2
      },
      md: {
        slidesPerView: 3,
        slidesPerGroup: 3
      },
      lg: {
        slidesPerView: anchorImage || metadata?.collectionImageUrl || isCategoryTab ? 4 : 6,
        slidesPerGroup: anchorImage || metadata?.collectionImageUrl || isCategoryTab ? 4 : 6
      },
      xl: {
        slidesPerView: anchorImage || metadata?.collectionImageUrl || isCategoryTab ? 4 : 6,
        slidesPerGroup: anchorImage || metadata?.collectionImageUrl || isCategoryTab ? 4 : 6
      }
    });
  } else if (slidesPer) {
    breakpoints = ({
      sm: {
        slidesPerView: slidesPer,
        slidesPerGroup: slidesPer
      },
      md: {
        slidesPerView: slidesPer,
        slidesPerGroup: slidesPer
      },
      lg: {
        slidesPerView: anchorImage || metadata?.collectionImageUrl || isCategoryTab ? 1 : slidesPer,
        slidesPerGroup: anchorImage || metadata?.collectionImageUrl || isCategoryTab ? 1 : slidesPer
      },
      xl: {
        slidesPerView: anchorImage || metadata?.collectionImageUrl || isCategoryTab ? 1 : slidesPer,
        slidesPerGroup: anchorImage || metadata?.collectionImageUrl || isCategoryTab ? 1 : slidesPer
      }
    });
  }

  let placeholderTxtLines;
  if (!isMobile && isCategoryTab) {
    placeholderTxtLines = 19;
  } else if (!isMobile && anchorImage) {
    placeholderTxtLines = 12;
  } else {
    placeholderTxtLines = 10;
  }

  return (
    <>
      {showLoading && loading && (
        showFallback && !userId && !products
          ? (
            <FallbackCard
              imageUrl={fallbackInfo.imageUrl}
              header={fallbackInfo.header}
              subheader={fallbackInfo.subheader}
              footerUrl={fallbackInfo.footerUrl}
              footer={fallbackInfo.footer}
            />
          )
          : <LoadingPlaceholder showMoreLines={(isCategoryTab)} txtPlaceholderLines={placeholderTxtLines} hideTitle={hideTitle} />
      )}
      {displayProducts?.length > 0 && (
        <>
          {!hideTitle && (
            isCategoryTab && !metadata?.collectionName ? (
              <CarouselHeader title="More From This Brand" />
            ) : (
              <CarouselHeader title={title} subtitle={subtitle} />
            )
          )}

          {isCategoryTab && (
            <div className={titleClasses}>
              <div>
                {metadata?.collectionTitle1 ? (
                  <div className="sui-ml-2 sui-pb-2 sm:sui-pb-0">
                    <Typography variant="h3">{metadata?.collectionTitle1}</Typography>
                  </div>
                ) : (
                  <div className="sui-ml-2 sui-pb-2 sm:sui-pb-0">
                    <Typography variant="h3">{products[0]?.product?.identifiers?.brandName}</Typography>
                  </div>
                )}
                {metadata?.collectionTitle2 && (
                  <div className="sui-ml-2 sui-pb-2 sm:sui-pb-0">
                    <Typography variant="body-base" weight="bold" component="h3">{metadata?.collectionTitle2}</Typography>
                  </div>
                )}
              </div>
              {!isMobile && (
                <div className={colSpanClasses}>
                  {isCategoryTab && categories?.length > 0 && categories?.length >= recsCollectionTabCount && (
                    // eslint-disable-next-line max-len
                    <VisualTabs products={products} categories={categories} filterItemIds={pullFilteredData} />
                  )}
                </div>
              )}
            </div>
          )}
          <div className={classes}>
            {!isMobile && anchorImage && (
              <div>
                <Image
                  src={anchorImage}
                  alt="main-product-image"
                  height="400"
                  width="400"
                  className="sui-w-full"
                  data-testid="anchor-image"
                />
                <div className="sui-bg-strong sui-text-center sui-p-1 sui-mb-4 sui-text-sm">Current Item</div>
              </div>
            )}
            {(() => {
              if (metadata?.collectionImageUrl || isCategoryTab) {
                return (
                  <CollectionsImage metadata={metadata} firstProductMedia={products[0]?.product?.media} isMobile={isMobile} />
                );
              }
              return null;
            })()}
            <div className={colSpanClasses}>
              <QueryProvider
                dataSource={scheme}
                cacheKey={`${scheme}-recommendations-carousel`}
                defaultVariables={defaultVariables}
              >
                <Carousel
                  breakpoints={breakpoints}
                  hideCarouselArrows={hideCarouselArrows}
                  disableMargin
                >
                  {displayProducts.map((dynamicProduct, idx) => {
                    const { product } = dynamicProduct;
                    const { itemId } = product;
                    const { preferredPriceFlag } = product.pricing;

                    return (
                      <Wrapper key={idx}>
                        <ProductPod
                          brandTitleMaxLine={brandTitleMaxLine}
                          customEventObject={customEventObject}
                          hideATC={hideAddToCart}
                          hideBulkPrice={hideBulkPrice}
                          hideFavorites={hideFavorites}
                          hideFulfillment={hideFulfillment}
                          hideLimitPerOrder={hideLimitPerOrder}
                          hidePriceBadge={hidePriceBadge}
                          hideRating={hideRating}
                          hideRebates={hideRebates}
                          hideSavingsText={!preferredPriceFlag}
                          hideShippingInfo={hideShippingInfo}
                          hideShopThisCollection={hideShopThisCollection}
                          itemId={itemId}
                          key={itemId}
                          position={idx}
                          showSubscribeAndSave={showSubscribeAndSave}
                          showPreferredPricingBadge
                          showSeeMoreOptions={hideSwatches}
                          storeId={storeId}
                          noATCFulfillment={isAppliance(product) ? false : noATCFulfillment}
                        />
                      </Wrapper>
                    );
                  }
                  )}
                </Carousel>
              </QueryProvider>
            </div>
          </div>
        </>
      )}
    </>
  );
};

const productModel = extend({}, {
  itemId: string(),
  dataSource: string(),
}, ProductPod.dataModel.product);

export const wrappedProduct = shape({
  category: string(),
  description: string(),
  idEndDate: string(),
  idPromoDetails: string(),
  idStartDate: string(),
  isAnchor: bool(),
  product: params({
    dataSource: string()
  })
    .shape(productModel),
  // itemId: string(),
  slot: shape({
    description: string(),
    isRequired: bool(),
    name: string(),
  })
});

RecommendationsProductPodCarousel.dataModel = extend(Wrapper, wrappedProduct);

RecommendationsProductPodCarousel.displayName = 'RecommendationsProductPodCarousel';

RecommendationsProductPodCarousel.propTypes = {
  ...ProductPod.propTypes,
  // eslint-disable-next-line react/forbid-prop-types
  data: any,
  loading: boolType,
  hideTitle: boolType,
  scheme: stringType,
  showLoading: boolType,
  storeId: stringType.isRequired,
  subtitle: stringType,

  // productPodProps
  // product-pod take only these values for brandTitleMaxLine prop
  brandTitleMaxLine: oneOf([2, 3, null]),
  customEventObject: shapeType({
    component: stringType,
    customEvent: stringType
  }),
  hideATC: boolType,
  hideBulkPrice: boolType,
  hideFavorites: boolType,
  hideFulfillment: boolType,
  hideLimitPerOrder: boolType,
  hidePriceBadge: boolType,
  hideRating: boolType,
  hideRebates: boolType,
  hideShopThisCollection: boolType,
  hideShippingInfo: boolType,
  showSubscribeAndSave: boolType,
  keepAddToCart: boolType,
  noATCFulfillment: boolType,
  isCategoryTab: boolType,
  hideSwatches: boolType,
  hideCarouselArrows: boolType
};

RecommendationsProductPodCarousel.defaultProps = {
  hideTitle: false,
  scheme: 'drecs',
  showLoading: true,
  loading: false,
  data: null,
  subtitle: null,

  brandTitleMaxLine: 3,
  customEventObject: null,
  hideATC: false,
  hideBulkPrice: false,
  hideFavorites: true,
  hideFulfillment: true,
  hideLimitPerOrder: true,
  hidePriceBadge: true,
  hideRating: false,
  hideRebates: true,
  hideShopThisCollection: true,
  hideShippingInfo: true,
  showSubscribeAndSave: false,
  keepAddToCart: null,
  noATCFulfillment: true,
  isCategoryTab: false,
  hideSwatches: true,
  hideCarouselArrows: false
};

export { RecommendationsProductPodCarousel };
